import { useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { CheckCircle2, XCircle, ExternalLink, AlertCircle } from 'lucide-react';
import { PageCard } from './PageCard';
import type { Post, KontentinoPage, PostAssignments, ScheduleResult } from '../types';

interface ResultsSummaryProps {
  posts: Post[];
  selectedPages: KontentinoPage[];
  postAssignments: PostAssignments;
  scheduleResults: ScheduleResult[];
  onClose: () => void;
}

export function ResultsSummary({
  posts,
  selectedPages,
  postAssignments,
  scheduleResults,
  onClose,
}: ResultsSummaryProps) {
  // Group successful posts by page
  const successfulResults = scheduleResults.filter((r) => r.success);
  const failedResults = scheduleResults.filter((r) => !r.success);

  const groupedByPage = useMemo(() => {
    const groups: Record<string, Array<{ post: Post; result: ScheduleResult }>> = {};

    for (const [pageId, postIds] of Object.entries(postAssignments)) {
      for (const postId of postIds) {
        const post = posts.find((p) => p.id === postId);
        const result = scheduleResults.find((r) => r.postId === postId);

        if (post && result && result.success) {
          if (!groups[pageId]) {
            groups[pageId] = [];
          }
          groups[pageId].push({ post, result });
        }
      }
    }

    return groups;
  }, [postAssignments, posts, scheduleResults]);

  const hasFailures = failedResults.length > 0;
  const allSuccessful = scheduleResults.length > 0 && failedResults.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
          {allSuccessful ? (
            <>
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Posts Scheduled Successfully!
            </>
          ) : (
            <>
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Scheduling Completed with Issues
            </>
          )}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {allSuccessful
            ? `🎉 ${successfulResults.length} ${successfulResults.length === 1 ? 'post has' : 'posts have'} been scheduled to your social media!`
            : `${successfulResults.length} scheduled, ${failedResults.length} failed`}
        </p>
      </div>

      {/* Successful posts grouped by page */}
      {Object.entries(groupedByPage).length > 0 && (
        <div className="space-y-3">
          {Object.entries(groupedByPage).map(([pageId, items]) => {
            const page = selectedPages.find((p) => p.id === pageId);
            if (!page) return null;

            return (
              <div key={pageId} className="border rounded-lg p-4 space-y-2 bg-card">
                <div className="flex items-center gap-2 font-semibold text-foreground pb-2 border-b">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <PageCard page={page} />
                  <span className="text-muted-foreground ml-auto">
                    ({items.length} {items.length === 1 ? 'post' : 'posts'})
                  </span>
                </div>

                <div className="space-y-1.5 pl-6">
                  {items.map(({ post, result }) => {
                    const dateTime = new Date(`${post.date}T${post.time}`);
                    const formatted = dateTime.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    });

                    return (
                      <div
                        key={result.postId}
                        className="text-sm flex items-start gap-2 group cursor-pointer hover:bg-accent/50 -mx-2 px-2 py-1 rounded transition-colors"
                        onClick={() => {
                          if (result.kontentinoPostUrl) {
                            window.open(result.kontentinoPostUrl, '_blank');
                          }
                        }}
                      >
                        <span className="text-muted-foreground">•</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-foreground flex items-center gap-1.5">
                            <span className="font-medium">{formatted}</span>
                            <span className="text-muted-foreground">-</span>
                            <span className="truncate">
                              {post.topic || post.text.slice(0, 50)}
                            </span>
                            {post.text.length > 50 && (
                              <span className="text-muted-foreground">...</span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            {result.kontentinoPostId && (
                              <>
                                <span>Post #{result.kontentinoPostId}</span>
                              </>
                            )}
                            <ExternalLink className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Failed posts */}
      {hasFailures && (
        <div className="border border-destructive/30 rounded-lg p-4 space-y-2 bg-destructive/5">
          <div className="flex items-center gap-2 font-semibold text-destructive">
            <XCircle className="h-4 w-4" />
            Failed Posts ({failedResults.length})
          </div>
          <div className="space-y-1.5 pl-6">
            {failedResults.map((result) => {
              const post = posts.find((p) => p.id === result.postId);
              if (!post) return null;

              return (
                <div key={result.postId} className="text-sm">
                  <div className="text-foreground">
                    {post.topic || post.text.slice(0, 50)}
                  </div>
                  <div className="text-xs text-destructive">
                    {result.error || 'Unknown error'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tip */}
      {successfulResults.length > 0 && (
        <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
          <div className="text-sm text-foreground">
            <span className="font-medium">💡 Tip:</span> Click any post above to view and edit it
            in Kontentino.
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={() => {
            window.open('https://app.kontentino.com', '_blank');
          }}
          className="gap-2"
        >
          <span>View in Kontentino</span>
          <ExternalLink className="h-4 w-4" />
        </Button>
        <Button onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}
